import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  ClipboardList,
  ExternalLink,
} from "lucide-react";

import { useGetMyAssessmentDietLogbooksQuery } from "../api/logbookAssessmentDietApi";

export default function AssessorAssessmentLogbooksPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } =
    useGetMyAssessmentDietLogbooksQuery(id, { skip: !id });

  const diet = data?.diet;
  const logbooks = Array.isArray(data?.data) ? data!.data : [];
  const summary = data?.summary ?? { total: 0, assessed: 0, pending: 0 };

  const isGraded = (lb: any) =>
    Boolean(lb?.graded_at) || String(lb?.status || "").toLowerCase() === "graded";

  // The assessor's recorded outcome for this logbook (pass / fail).
  const resultOf = (lb: any) =>
    String(lb?.assessments?.[0]?.result ?? "").toLowerCase();

  // Review + grade both happen on the assessor logbook detail page; the diet id
  // is carried so grading is recorded against this assessment diet.
  const openReview = (lb: any) =>
    navigate(`/assessor/logbook/${lb.id}?diet=${encodeURIComponent(id)}`);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate("/assessor/assessments")}
          className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-4 hover:text-indigo-700"
        >
          <ArrowLeft size={14} /> Back to my assessments
        </button>

        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-black text-indigo-900 flex items-center gap-2">
              <ClipboardList className="text-indigo-600" size={24} />
              {diet?.title || "Assessment diet"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Logbooks assigned to you in this diet. Open one to review the
              entries and record your assessment.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="text-xs font-semibold border px-3 py-1.5 rounded-lg bg-white hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="rounded-2xl bg-white border p-4">
            <div className="text-2xl font-black text-indigo-700">{summary.total}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase">Assigned</div>
          </div>
          <div className="rounded-2xl bg-white border p-4">
            <div className="text-2xl font-black text-amber-600 flex items-center gap-1">
              <Clock size={18} /> {summary.pending}
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase">Pending</div>
          </div>
          <div className="rounded-2xl bg-white border p-4">
            <div className="text-2xl font-black text-emerald-600 flex items-center gap-1">
              <CheckCircle2 size={18} /> {summary.assessed}
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase">Assessed</div>
          </div>
        </div>

        {isLoading && <p className="text-sm text-gray-500">Loading logbooks…</p>}
        {isError && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm p-4 rounded-xl">
            Failed to load logbooks (you may not be an assessor on this diet).
          </div>
        )}

        {!isLoading && !isError && (
          <div className="bg-white border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Candidate</th>
                    <th className="text-left font-semibold px-4 py-3">Stage</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="text-left font-semibold px-4 py-3">Result</th>
                    <th className="text-left font-semibold px-4 py-3">Score</th>
                    <th className="text-right font-semibold px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logbooks.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-sm">
                        No logbooks assigned to you in this diet.
                      </td>
                    </tr>
                  )}
                  {logbooks.map((lb: any) => (
                    <tr key={lb.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">
                          {lb?.user?.name || "—"}
                        </div>
                        <div className="text-xs text-gray-400">{lb?.user?.email}</div>
                      </td>
                      <td className="px-4 py-3">Stage {lb?.stage || 1}</td>
                      <td className="px-4 py-3">
                        {isGraded(lb) ? (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                            assessed
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                            awaiting
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const r = resultOf(lb);
                          if (!r) return <span className="text-gray-300">—</span>;
                          return (
                            <span
                              className={`text-[11px] font-bold px-2 py-0.5 rounded-full border capitalize ${
                                r === "pass"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-red-50 text-red-700 border-red-200"
                              }`}
                            >
                              {r}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-700">
                        {lb?.total_score != null ? `${lb.total_score}/100` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => openReview(lb)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 ${
                              isGraded(lb)
                                ? "border bg-white hover:bg-gray-50 text-gray-700"
                                : "bg-indigo-600 text-white hover:bg-indigo-700"
                            }`}
                          >
                            <ExternalLink size={13} />
                            {isGraded(lb) ? "View" : "Review & grade"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
